import * as React from "react";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";
import { useForm } from "react-hook-form";
import { $enum } from "ts-enum-util";
import styled, { css } from "styled-components";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { Button } from "src/library/components/Button";
import { DateRangePickerCompact } from "src/library/components/DateRangePickerCompact";
import {
  Label,
  Select,
  FormRow,
  IFormOption
} from "src/library/components/Forms";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import {
  CheckboxLabel,
  CheckboxStyled
} from "src/library/components/Forms/Checkbox";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { IOrder } from "src/core/orders/api/orders.api";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import { orderExportTableColumns } from "src/core/shared/components/tables/orderExportTableColumns";
import {
  useOrderExportApi,
  IOrderExport,
  OrderExportType
} from "src/modules/order-export/api/order-export.api";
import DownloadOrderExport from "src/modules/order-export/components/OrderExportDownload";
import { CustomerMultiSelect } from "src/core/customers/components/CustomerMultiSelect";
import { ICustomer } from "src/core/customers/api/customers.api";
import { usePaymentApi } from "src/modules/payment/api/payment.api";
import { TransactionType } from "src/modules/payment/api/transactions.api";
import { DateInput } from "src/core/shared/components/Date/DateInput";
import { TimePicker } from "src/core/shared/components/Date/TimePicker";
import { hourMinuteTimeSplit } from "src/library/utils/time-helpers/time-helpers";
import { Tooltip } from "src/library/components/Tooltip";
import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";

interface ExportFiltersFormAttributes {
  exportOption: string;
  fromDate: Date;
  toDate: Date;
  orderStatus?: string;
  customers?: Array<NonNullable<ICustomer["id"]>>;
  transactionType: TransactionType[]
  archiveMockDateTime?: Date | null;
}

/**
 * Export orders page
 */
const ExportOrdersPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology } = props;
  const today = new Date();
  const maxFromDate = moment.utc().subtract(10, "years").toDate();
  const maxToDate = moment.utc().add(10, "years").toDate();
  const defaultFromDate = moment.utc(moment.utc().toDate().setUTCHours(0, 0, 0, 0)).toDate();
  const defaultToDate = moment.utc(moment.utc().toDate().setUTCHours(23, 59, 59, 59)).toDate();
  const TableRef = React.useRef<ITableInstance<IOrderExport>>(null);

  const { orderStatuses, installedModules } = useMappedState((store: IStore) => ({
    installedModules: store.global.settings?.installedModules || [],
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : []
  }));

  const [ listExportsRes, listExportsReq ] = useOrderExportApi("LIST_EXPORTS");
  const [ exportOrdersRes, exportOrdersReq ] = useOrderExportApi("EXPORT_ORDERS");
  const [ exportOrderSettingsRes, exportOrderSettingsReq ] = useOrderExportApi("LIST_ORDER_EXPORT_SETTINGS");
  const [ generateFinancialReportRes, generateFinancialReportReq ] = usePaymentApi("GENERATE_FINANCIAL_REPORT");

  const {
    handleSubmit, register, watch, setValue
  } = useForm<ExportFiltersFormAttributes>({ mode: "onChange" });

  const watchValues = watch([
    "exportOption",
    "fromDate",
    "toDate",
    "transactionType",
    "archiveMockDateTime"
  ], {
    exportOption: "1",
    fromDate: defaultFromDate,
    toDate: defaultToDate,
    transactionType: [],
    archiveMockDateTime: null
  });

  const currentSettings = React.useMemo(() => {
    const selectedExport = _.find(exportOrderSettingsRes.data, setting => setting.id.toString() === watchValues.exportOption);

    return selectedExport;
  }, [ exportOrderSettingsRes.data, watchValues.exportOption ]);

  const orderStatusFormValues = React.useMemo(() => {
    const orderStatusesDefaultOption = [
      {
        id: "-1",
        name: "All orders"
      }
    ];

    const orderStatusesOptions = orderStatuses.map(orderStatus => ({
      id: orderStatus.id.toString(),
      name: orderStatus.name
    }));

    return orderStatusesDefaultOption.concat(orderStatusesOptions);
  }, [ orderStatuses ]);

  /**
   * On exports table fetch, perform a list exports request via the API
   */
  const onFetchData = React.useCallback((filters: FilterParams<IOrderExport>, pagination: PaginatedParams<IOrderExport>) => {
    listExportsReq({
      pagination,
      filters
    });
  }, [ listExportsReq ]);

  /**
   * Handles the form submission.
   *
   * Set the export filters for the export orders API request
   */
  const submitForm = React.useCallback((values: ExportFiltersFormAttributes) => {
    const exportFilters: FilterParams<IOrder> = {};

    if (currentSettings) {
      if (currentSettings.type === "archive") {
        if (!values.archiveMockDateTime) {
          toast.error("Please select a valid archive date time");

          return;
        }

        // Perform the export orders request via the API
        exportOrdersReq({
          orderExportSettingId: currentSettings.id,
          archiveMockDateTime: moment.utc(values.archiveMockDateTime).toDate(),
          filters: {}
        });
      } else if (currentSettings?.type === "report") {
        // Set the filters for the "date_range" export option
        const startDate = values.fromDate;
        const endDate = values.toDate;

        // Set the orderDeliveryDate filter
        exportFilters.orderDeliveryDate = { $between: [ startDate, endDate ] };

        // Set the orderStatusId filter
        if (values.orderStatus && values.orderStatus !== "-1") {
          exportFilters.orderStatusId = parseInt(values.orderStatus, 10);
        }

        // Set the
        if (values.customers && values.customers.length > 0) {
          exportFilters.branch = { customerId: { $in: values.customers } };
        }

        // Perform the export orders request via the API
        exportOrdersReq({
          orderExportSettingId: currentSettings.id,
          filters: exportFilters
        });
      } else if (values.exportOption === "payment_reconciliation") {
        generateFinancialReportReq({
          fromDate: values.fromDate,
          toDate: values.toDate
        });
      } else {
        exportOrdersReq({
          orderExportSettingId: currentSettings.id,
          filters: exportFilters
        });
      }
    }
  }, [
    currentSettings,
    exportOrdersReq,
    generateFinancialReportReq
  ]);

  // On mount, register the following virtual form fields
  React.useEffect(() => {
    exportOrderSettingsReq(null);
    register("fromDate");
    register("toDate");
    register("orderStatus");
    register("customers");
    register("transactionType");
    register("archiveMockDateTime");
    // Set the value as default values - watch does not work
    setValue("fromDate", maxFromDate);
    setValue("toDate", maxToDate);
    setValue("fromDate", defaultFromDate);
    setValue("toDate", defaultToDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On exportOrdersRes change
  React.useEffect(() => {
    // If an error occurs, show the error message
    if (exportOrdersRes.error) {
      toast.error(`Could not export ${terminology.order.singular.text} - ${exportOrdersRes.error.message}`);
    }

    // On success, show a success message
    if (exportOrdersRes.data) {
      toast.success("Order export start! You will receive an email shortly with a file attached.");
    }
  // Ignore other dependencies, we only want to update on exportOrdersRes change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ exportOrdersRes.data, exportOrdersRes.error ]);

  React.useEffect(() => {
    if (generateFinancialReportRes.data) {
      toast.success("Export start! You will receive an email shortly with a file attached.");
    } else if (generateFinancialReportRes.error) {
      toast.error(generateFinancialReportRes.error.message);
    }
  }, [ generateFinancialReportRes.data, generateFinancialReportRes.error ]);

  const exportOptions = React.useMemo(() => {
    let options: IFormOption[] = [];

    if (exportOrderSettingsRes.data) {
      options = _.map(_.filter(exportOrderSettingsRes.data, exportSetting => !exportSetting.options.hidden), exportSetting => ({
        text: exportSetting.name,
        value: exportSetting.id.toString()
      }));
    } else {
      return [];
    }

    if (installedModules.includes("payment")) {
      options.push({
        value: "payment_reconciliation",
        text: "Payout reconciliation"
      });
    }

    return options;
  }, [ exportOrderSettingsRes ]);

  return (
    <Layout {...props}>
      <OrderExportPageStyled fill>
        <form onSubmit={handleSubmit(submitForm)}>
          <FormRow>
            <Select
              ref={register()}
              as="select"
              name="exportOption"
              label="Export orders"
              className="select-box"
              autoHideLabels
              options={exportOptions}
              unselectedOption={{
                text: "Please select",
                value: ""
              }}
            />
          </FormRow>

          {currentSettings?.type === OrderExportType.Archive && (
            <FormRow>
              <div className="archive-label">
                <Label
                  for=""
                  label="Select the mock date and time for the archive:"
                />

                <div
                  className="help-icon"
                  data-tip
                  data-for="archive-help"
                >
                  <Icon
                    icon={Icons.HELP}
                    size={10}
                  />
                </div>
              </div>

              <Tooltip
                id="archive-help"
                type="dark"
                place="bottom"
                getContent={() => {
                  return (
                    <Text color="white">
                      Set the date and time to be after the cutoff time you are attempting mimic.
                    </Text>
                  );
                }}
              />

              <div className="archive-datetime-row">
                <div className="archive-datetime">
                  <DateInput
                    label={watchValues.archiveMockDateTime ? moment.utc(watchValues.archiveMockDateTime).local().format("DD/MM/YYYY") : "Archive date"}
                    setDate={archiveDate => setValue("archiveMockDateTime", archiveDate)}
                    minimumDate={new Date()}
                  />

                  {watchValues.archiveMockDateTime && (
                    <TimePicker
                      displayTimezone={false}
                      name="Archive time"
                      timezone="Europe/London"
                      value={moment.utc(watchValues.archiveMockDateTime).local().format("HH:mm")}
                      setTime={time => {
                        const archiveMockDateTime = watchValues.archiveMockDateTime;

                        if (archiveMockDateTime) {
                          const { hours, minutes } = hourMinuteTimeSplit(time);

                          archiveMockDateTime.setHours(hours);
                          archiveMockDateTime.setMinutes(minutes);
                          setValue("archiveMockDateTime", archiveMockDateTime);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </FormRow>
          )}

          {(currentSettings?.type === OrderExportType.Report || watchValues.exportOption === "payment_reconciliation") && (
            <>
              <FormRow>
                <Label
                  for=""
                  label="Select an export date range:"
                />

                <DateRangePickerCompact
                  date={today}
                  minDate={maxFromDate}
                  maxDate={maxToDate}
                  // if date range not selected, display default range as apposed to please select prompt
                  selectDatesTitle={`${moment.utc(defaultFromDate).format("ddd DD MMM YYYY")} - ${moment.utc(defaultToDate).format("ddd DD MMM YYYY")}`}
                  onDateChange={({ from, to }) => {
                  // If the "from" date is Set, update the fromDate value
                    if (from) {
                      from.setUTCHours(0, 0, 0, 0);
                      setValue("fromDate", from);
                    }

                    // If the "to" date is Set, update the toDate value
                    if (to) {
                      to.setUTCHours(23, 59, 59, 59);
                      setValue("toDate", to);
                    }
                  }}
                />
              </FormRow>

              {watchValues.exportOption === "payment_reconciliation" && (
                <FormRow>
                  <Label
                    for=""
                    label="Select the types of transaction you want export"
                  />

                  {_.map($enum(TransactionType).getValues(), type => (
                    <CheckboxLabel
                      key={type}
                      for={`category-${type}`}
                      className="cat-label"
                      label={type}
                    >
                      <CheckboxStyled
                        className="styled-checkbox"
                        id={`category-${type}`}
                        value={type}
                        onChange={e => {
                          if (e.currentTarget.checked) {
                            setValue("transactionType", [ ...watchValues.transactionType, type ]);
                          } else {
                            setValue("transactionType", _.remove(watchValues.transactionType, tt => tt !== type));
                          }
                        }}
                      />
                    </CheckboxLabel>
                  )
                  )}

                </FormRow>
              )}

              {currentSettings?.type === OrderExportType.Report && (
                <>
                  <FormRow>
                    <Label
                      for="orderStatus"
                      label="Select a order status:"
                    />

                    <Select
                      ref={register()}
                      as="select"
                      name="orderStatus"
                      label=""
                      options={_.map(orderStatusFormValues, status => ({
                        text: status.name,
                        value: status.id
                      }))}
                    />
                  </FormRow>

                  <FormRow>
                    <CustomerMultiSelect
                      onCustomersChange={({ customersSelected }) => setValue("customers", customersSelected.map(cid => parseInt(cid, 10)))}
                    />
                  </FormRow>
                </>
              )}

            </>
          )}

          {currentSettings?.description && (
            <FormRow>
              <ErrorNotice
                type="warn"
                message={currentSettings?.description}
              />
            </FormRow>
          )}

          <FormRow>
            <Button
              type="submit"
              $buttonType="primary"
              size="medium"
              disabled={!currentSettings || (currentSettings.type === OrderExportType.Report && (!watchValues.fromDate || !watchValues.toDate))}
              loading={exportOrdersRes.pending}
            >
              {`Export ${terminology.order.plural.text}`}
            </Button>
          </FormRow>
        </form>

        <Table
          tableId="orderExports"
          ref={TableRef}
          tableOf={`${terminology.product.singular.text} prices`}
          data={listExportsRes.data?.result || []}
          columns={orderExportTableColumns(row => {
            return <DownloadOrderExport orderExport={row.original} />;
          }, exportOrderSettingsRes.data)}
          loading={listExportsRes.pending}
          error={listExportsRes.error}
          onFetchData={onFetchData}
          pages={listExportsRes.data?.pages || 0}
          totalRows={listExportsRes.data?.count || 0}
        />
      </OrderExportPageStyled>
    </Layout>
  );
};

export default ExportOrdersPage;

const OrderExportPageStyled = styled(MainContentWrapper)`
  ${({ theme }) => css`
    .archive-datetime-row {
      width: 400px;
      margin-bottom: ${theme.spacing.margins.md};
      .archive-datetime {
        display: flex;
        flex-direction: row;
      }
    }
    .archive-label {
      display: flex;
    }
  `}
`;
